import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Alert, Button, CircularProgress, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ROUTES from '../../common/Routing/Routes';
import { useCart } from '../../../contexts/CartContext';
import { toAbsoluteUrl } from '../../common/Utils/BookImages';
import { getShopItem, Merchandise, merchandiseCategoryLabels } from '../../common/Rest/Services/ShopService';
import { formatShopPrice, getShopImageUrl, toShopCartItem } from './shopUtils';
import ShopInquiryDialog from './ShopInquiryDialog';
import './ShopPage.css';

function ShopItemPage() {
  const { id } = useParams();
  const { addToCart, isInCart, openCartModal } = useCart();
  const [item, setItem] = useState<Merchandise | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(0);
  const [inquiryOpen, setInquiryOpen] = useState(false);

  useEffect(() => {
    const itemId = Number(id);
    if (!Number.isFinite(itemId)) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setActiveImage(0);
    getShopItem(itemId)
      .then(setItem)
      .catch(() => setItem(null))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="shop-page default-container">
        <CircularProgress className="shop-loading" />
      </div>
    );
  }

  if (!item) {
    return (
      <div className="shop-page default-container">
        <p className="shop-empty">Товар не знайдено.</p>
        <Link to={ROUTES.SHOP} className="shop-back-link">
          <ArrowBackIcon fontSize="small" /> До крамнички
        </Link>
      </div>
    );
  }

  const images = item.images.length > 0 ? item.images : [];
  const currentImage = images[activeImage];
  const mainImageUrl = currentImage
    ? toAbsoluteUrl(currentImage.mediumUrl || currentImage.url) || getShopImageUrl(item)
    : getShopImageUrl(item);
  const inCart = isInCart(item.id);
  const isAvailable = item.stockCount > 0;
  const paragraphs = item.description?.split(/\n+/).filter((text) => text.trim()) ?? [];

  const handleAddToCart = () => {
    if (inCart) {
      openCartModal();
      return;
    }

    addToCart(toShopCartItem(item));
  };

  return (
    <div className="shop-page default-container">
      <Link to={ROUTES.SHOP} className="shop-back-link">
        <ArrowBackIcon fontSize="small" /> До крамнички
      </Link>

      <section className="shop-item">
        <div className="shop-gallery">
          <div className="shop-gallery-main">
            <img src={mainImageUrl} alt={item.name} />
          </div>
          {images.length > 1 && (
            <div className="shop-gallery-thumbs" role="list">
              {images.map((image, index) => (
                <button
                  key={image.id}
                  type="button"
                  role="listitem"
                  className={index === activeImage ? 'active' : ''}
                  onClick={() => setActiveImage(index)}
                  aria-label={`Фото ${index + 1}`}
                >
                  <img src={toAbsoluteUrl(image.thumbUrl || image.url)} alt="" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="shop-item-main">
          <Typography variant="overline">{merchandiseCategoryLabels[item.category] ?? 'Крамничка'}</Typography>
          <Typography variant="h3" component="h1">{item.name}</Typography>
          <Typography className="shop-item-price">{formatShopPrice(item.price)}</Typography>

          <Alert severity={isAvailable ? 'success' : 'warning'}>
            {isAvailable ? `В наявності: ${item.stockCount} шт.` : 'Зараз немає в наявності'}
          </Alert>

          <div className="shop-item-actions">
            <Button
              variant="contained"
              size="large"
              startIcon={<ShoppingBagOutlinedIcon />}
              disabled={!isAvailable && !inCart}
              onClick={handleAddToCart}
            >
              {inCart ? 'До кошика' : 'Купити'}
            </Button>
            <Button
              variant="outlined"
              size="large"
              startIcon={<ChatBubbleOutlineIcon />}
              onClick={() => setInquiryOpen(true)}
            >
              Уточнити деталі
            </Button>
          </div>
          <ShopInquiryDialog
            open={inquiryOpen}
            itemId={item.id}
            itemName={item.name}
            onClose={() => setInquiryOpen(false)}
          />

          {paragraphs.length > 0 && (
            <div className="shop-item-description">
              {paragraphs.map((text, index) => (
                <p key={index}>{text}</p>
              ))}
            </div>
          )}

          {item.details && (
            <div className="shop-item-details">
              <Typography variant="subtitle2">Характеристики</Typography>
              <p>{item.details}</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

export default ShopItemPage;
